import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth, type Role } from '@/shared/auth/AuthProvider';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { PlaceholderPage } from '@/features/placeholder/PlaceholderPage';
import { ExpurgoPage } from '@/features/expurgo/ExpurgoPage';
import { LavagemPage } from '@/features/lavagem/LavagemPage';
import { PreparoPage } from '@/features/preparo/PreparoPage';
import { EsterilizacaoPage } from '@/features/esterilizacao/EsterilizacaoPage';
import { LiberacaoPage } from '@/features/liberacao/LiberacaoPage';
import { ArsenalPage } from '@/features/arsenal/ArsenalPage';
import { PointOfCarePage } from '@/features/cirurgia/PointOfCarePage';
import { QuarentenaPage } from '@/features/quarentena/QuarentenaPage';
import { RecallPage } from '@/features/recall/RecallPage';
import { ManutencaoPage } from '@/features/manutencao/ManutencaoPage';
import { SetoresPage } from '@/features/admin/SetoresPage';
import { FornecedoresPage } from '@/features/admin/FornecedoresPage';
import { LocaisPage } from '@/features/admin/LocaisPage';
import { EmbalagensPage } from '@/features/admin/EmbalagensPage';
import { EspecialidadesPage } from '@/features/admin/EspecialidadesPage';
import { ItemModelsPage } from '@/features/admin/ItemModelsPage';
import { UsuariosPage } from '@/features/admin/UsuariosPage';
import { EquipamentosPage } from '@/features/admin/EquipamentosPage';
import { ItensPage } from '@/features/admin/ItensPage';
import { KitTemplatesPage } from '@/features/admin/KitTemplatesPage';
import { AuditoriaPage } from '@/features/admin/AuditoriaPage';
import { ConfiguracoesPage } from '@/features/admin/ConfiguracoesPage';
import { OrganizacaoPage } from '@/features/admin/OrganizacaoPage';
import { FiliaisPage } from '@/features/admin/FiliaisPage';
import { AjudaPage } from '@/features/help/AjudaPage';
import { SalaSujaPage } from '@/features/dirtyroom/SalaSujaPage';

function RequireAuth({ children, roles }: { children: ReactNode; roles?: Role[] }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="grid h-full place-items-center text-sm text-neutral-500">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (roles && !roles.includes(user.role)) {
    return (
      <PlaceholderPage
        title="Acesso negado"
        description={`Seu perfil (${user.role}) não tem permissão para esta área.`}
      />
    );
  }
  return <>{children}</>;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<DashboardPage />} />
        <Route path="expurgo"       element={<ExpurgoPage />} />
        <Route path="lavagem"       element={<LavagemPage />} />
        <Route path="preparo"       element={<PreparoPage />} />
        <Route path="esterilizacao" element={<EsterilizacaoPage />} />
        <Route path="liberacao"     element={<RequireAuth roles={['ADMIN','SUPERVISOR','NURSE_RT']}><LiberacaoPage /></RequireAuth>} />
        <Route path="arsenal"       element={<ArsenalPage />} />
        <Route path="cirurgia"      element={<PointOfCarePage />} />
        <Route path="quarentena"    element={<QuarentenaPage />} />
        <Route path="recall"        element={<RequireAuth roles={['ADMIN','SUPERVISOR','NURSE_RT']}><RecallPage /></RequireAuth>} />
        <Route path="manutencao"    element={<RequireAuth roles={['ADMIN','SUPERVISOR','NURSE_RT']}><ManutencaoPage /></RequireAuth>} />
        <Route path="equipamentos"  element={<RequireAuth roles={['ADMIN','SUPERVISOR','NURSE_RT']}><EquipamentosPage /></RequireAuth>} />
        <Route path="setores"       element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><SetoresPage /></RequireAuth>} />
        <Route path="locais"        element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><LocaisPage /></RequireAuth>} />
        <Route path="fornecedores"  element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><FornecedoresPage /></RequireAuth>} />
        <Route path="embalagens"    element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><EmbalagensPage /></RequireAuth>} />
        <Route path="especialidades" element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><EspecialidadesPage /></RequireAuth>} />
        <Route path="item-models"   element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><ItemModelsPage /></RequireAuth>} />
        <Route path="itens"         element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><ItensPage /></RequireAuth>} />
        <Route path="kits"          element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><KitTemplatesPage /></RequireAuth>} />
        <Route path="catalogos"     element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><PlaceholderPage title="Catálogos" description="Use os atalhos no menu lateral: Modelos de Item, Kits, Embalagens, Especialidades." /></RequireAuth>} />
        <Route path="usuarios"      element={<RequireAuth roles={['ADMIN']}><UsuariosPage /></RequireAuth>} />
        <Route path="organizacao"   element={<RequireAuth roles={['ADMIN']}><OrganizacaoPage /></RequireAuth>} />
        <Route path="filiais"       element={<RequireAuth roles={['ADMIN']}><FiliaisPage /></RequireAuth>} />
        <Route path="auditoria"     element={<RequireAuth roles={['ADMIN','SUPERVISOR']}><AuditoriaPage /></RequireAuth>} />
        <Route path="configuracoes" element={<RequireAuth roles={['ADMIN']}><ConfiguracoesPage /></RequireAuth>} />
        <Route path="ajuda"          element={<AjudaPage />} />
        <Route path="sala-suja"      element={<SalaSujaPage />} />
        <Route path="*"             element={<PlaceholderPage title="Página não encontrada" description="Verifique o endereço." />} />
      </Route>
    </Routes>
  );
}
